// StatusPill — color-blind-safe status chip.
// Always shows icon + label (never hue alone).
// Uses admin-only --color-status-* tokens defined in styles.css @theme block.

import {
  AlertTriangle,
  CheckCircle2,
  CircleSlash,
  Clock,
  FileText,
  Lock,
  UserCheck,
  XCircle,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@awakenedlands/ui';

export type PillStatus =
  | 'active'
  | 'locked'
  | 'inactive'
  | 'pending'
  | 'claimed'
  | 'resolved'
  | 'ok'
  | 'stale'
  | 'missing'
  | 'published'
  | 'scheduled'
  | 'draft'
  | 'expired'
  | 'completed'
  | 'reversed'
  | 'failed'
  | 'payment_pending';

interface PillConfig {
  icon: ReactNode;
  /** Tailwind arbitrary-value text color using the status token. */
  colorClass: string;
  bgClass: string;
  borderClass: string;
  label: string;
}

const PILL_CONFIG: Record<PillStatus, PillConfig> = {
  active: {
    icon: <CheckCircle2 size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-ok]',
    bgClass: 'bg-[--color-status-ok]/15',
    borderClass: 'border border-[--color-status-ok]/35',
    label: 'active',
  },
  locked: {
    icon: <Lock size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-bad]',
    bgClass: 'bg-[--color-status-bad]/15',
    borderClass: 'border border-[--color-status-bad]/35',
    label: 'locked',
  },
  inactive: {
    icon: <CircleSlash size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-warn]',
    bgClass: 'bg-[--color-status-warn]/15',
    borderClass: 'border border-[--color-status-warn]/35',
    label: 'inactive',
  },
  pending: {
    icon: <Clock size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-warn]',
    bgClass: 'bg-[--color-status-warn]/15',
    borderClass: 'border border-[--color-status-warn]/35',
    label: 'pending',
  },
  claimed: {
    icon: <UserCheck size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-info]',
    bgClass: 'bg-[--color-status-info]/15',
    borderClass: 'border border-[--color-status-info]/35',
    label: 'claimed',
  },
  resolved: {
    icon: <CheckCircle2 size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-ok]',
    bgClass: 'bg-[--color-status-ok]/15',
    borderClass: 'border border-[--color-status-ok]/35',
    label: 'resolved',
  },
  ok: {
    icon: <CheckCircle2 size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-ok]',
    bgClass: 'bg-[--color-status-ok]/15',
    borderClass: 'border border-[--color-status-ok]/35',
    label: 'ok',
  },
  stale: {
    icon: <AlertTriangle size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-warn]',
    bgClass: 'bg-[--color-status-warn]/15',
    borderClass: 'border border-[--color-status-warn]/35',
    label: 'stale',
  },
  missing: {
    icon: <XCircle size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-bad]',
    bgClass: 'bg-[--color-status-bad]/15',
    borderClass: 'border border-[--color-status-bad]/35',
    label: 'missing',
  },
  // Newspaper CMS domain statuses (parallel to the generic set above but with
  // publishing-accurate labels so staff see Published/Scheduled/Draft/Expired).
  published: {
    icon: <CheckCircle2 size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-ok]',
    bgClass: 'bg-[--color-status-ok]/15',
    borderClass: 'border border-[--color-status-ok]/35',
    label: 'Published',
  },
  scheduled: {
    icon: <Clock size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-info]',
    bgClass: 'bg-[--color-status-info]/15',
    borderClass: 'border border-[--color-status-info]/35',
    label: 'Scheduled',
  },
  draft: {
    icon: <FileText size={13} aria-hidden="true" />,
    colorClass: 'text-muted-foreground',
    bgClass: 'bg-muted/40',
    borderClass: 'border border-border',
    label: 'Draft',
  },
  expired: {
    icon: <AlertTriangle size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-warn]',
    bgClass: 'bg-[--color-status-warn]/15',
    borderClass: 'border border-[--color-status-warn]/35',
    label: 'Expired',
  },
  // Payment domain statuses (parallel to the generic set above but with
  // payment-accurate labels so staff see Completed/Reversed/Pending/Failed).
  // payment_status strings mapped case-insensitively in paymentStatusToPill().
  completed: {
    icon: <CheckCircle2 size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-ok]',
    bgClass: 'bg-[--color-status-ok]/15',
    borderClass: 'border border-[--color-status-ok]/35',
    label: 'Completed',
  },
  reversed: {
    icon: <AlertTriangle size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-warn]',
    bgClass: 'bg-[--color-status-warn]/15',
    borderClass: 'border border-[--color-status-warn]/35',
    label: 'Reversed',
  },
  failed: {
    icon: <XCircle size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-bad]',
    bgClass: 'bg-[--color-status-bad]/15',
    borderClass: 'border border-[--color-status-bad]/35',
    label: 'Failed',
  },
  // Payment-domain "Pending" (title-case, matching the payment set above).
  // Distinct from the generic lowercase `pending` pill used by other surfaces.
  payment_pending: {
    icon: <Clock size={13} aria-hidden="true" />,
    colorClass: 'text-[--color-status-warn]',
    bgClass: 'bg-[--color-status-warn]/15',
    borderClass: 'border border-[--color-status-warn]/35',
    label: 'Pending',
  },
};

interface StatusPillProps {
  status: PillStatus;
  className?: string;
}

/** Map a raw payment_status string (case-insensitive) to the PillStatus enum. */
export function paymentStatusToPill(raw: string): PillStatus {
  switch (raw.toLowerCase()) {
    case 'completed': return 'completed';
    case 'reversed':  return 'reversed';
    case 'pending':   return 'payment_pending';
    case 'failed':    return 'failed';
    default:          return 'payment_pending';
  }
}

export function StatusPill({ status, className }: StatusPillProps) {
  const cfg = PILL_CONFIG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs font-medium',
        cfg.colorClass,
        cfg.bgClass,
        cfg.borderClass,
        className,
      )}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}
